<template>
  <div class="answers">
    <div v-if="!answerTypeText" class="answers__selection-wrapper">
      <div
        v-for="answer in answers"
        :key="`answer-id-${answerId(answer)}`"
        :class="[
          'answers__selection',
          isSelected(answer) && 'answers__selection--selected',
        ]"
      >
        <label class="answer-checkbox answers__select answers__checkboxes">
          <span class="answer-checkbox__input">
            <input
              v-model="currentSelection"
              :class="[isSelected(answer) && 'answer-checkbox--checked']"
              :type="currentQuestion.widget || 'checkbox'"
              :name="`${answerId(answer)}-checkbox`"
              :value="answerId(answer)"
            />
            <span
              :class="
                `answer-checkbox__control answer-checkbox__control--${currentQuestion.widget ||
                  'checkbox'}`
              "
            >
              <span
                v-if="currentQuestion.widget === 'radio'"
                class="answer-checkbox__radio-dot"
              />
              <CheckboxTick v-else />
            </span>
          </span>
          <div class="answers__answer">
            <span v-html="answerContent(answer)"></span>
          </div>
        </label>
      </div>
    </div>
    <AnswerTextbox
      v-if="answerTypeText"
      v-bind="{
        currentQuestion,
        attempt,
        isLabRunning,
        currentQuestionId,
      }"
      @update-selection="updateSelectedAnswers"
      @set-disabled="setDisabled"
    />
    <div
      v-if="!isLoading"
      :class="[
        'answers__btn-wrapper',
        examLayoutV2 && 'answers__btn-wrapper--layout-v2',
      ]"
    >
      <div
        class="answers__action-button"
        :style="
          currentQuestionId === firstQuestionId
            ? 'pointer-events: none; opacity: 0.5'
            : ''
        "
      >
        <button
          class="btn btn--secondary btn--secondary--with-icon btn--secondary--with-icon--left answers__btn answers__btn__prev btn--default-icon-color"
          :disabled="!jumpQuestionEnabled"
          @click="previousQuestion()"
        >
          <ArrowLeftIcon class="answers__btn__prev--icon" />
          {{ previousButtonLabel }}
        </button>
      </div>
      <div
        v-if="showActionButtons"
        class="answers__action-buttons answers__action-buttons__tools"
      >
        <span
          v-if="isBeta"
          v-tooltip="{
            content: 'Provide feedback on this question',
            placement: 'top-center',
          }"
          class="answers__action-buttons__wrapper"
          :class="{ 'answers__action-buttons__wrapper--active': hasFeedback }"
          @click="$emit('open-question-feedback')"
          ><FeedbackIcon
        /></span>
        <span
          v-tooltip="{
            content: tooltipText,
            placement: 'top-center',
          }"
          class="answers__action-buttons__wrapper answers__action-buttons__wrapper-pin"
          :class="{ 'answers__action-buttons__wrapper--active': hasFlagged }"
          @click="$emit('flag-question')"
          ><PinIcon
        /></span>
      </div>
      <div class="answers__action-buttons answers__action-buttons__with-time">
        <button
          v-if="!isLastQuestion || showFinished"
          class="btn btn--primary btn--primary--with-icon btn--primary--with-icon--right answers__btn answers__btn__next btn--default-icon-color"
          :disabled="!jumpQuestionEnabled"
          @click="answerQuestion()"
        >
          {{ nextButtonLabel }}
          <ArrowRightIcon class="answers__btn__next--icon" />
        </button>
      </div>
      <div
        v-if="humanSavedTime"
        :class="[
          'answers__saved-time',
          isLastQuestion && 'answers__saved-time--last-question',
        ]"
      >
        <span class="answers__saved-time--highlighted">
          <CheckboxTick /> Saved.
        </span>
        Progress saved {{ humanSavedTime }}.
      </div>
    </div>
    <div
      v-else
      :class="[
        'answers__btn-wrapper',
        'answers__saving-wrapper',
        examLayoutV2 && 'answers__btn-wrapper--layout-v2',
      ]"
    >
      <div class="answers__saving">
        <span class="dot-flashing"></span>Saving
      </div>
    </div>
  </div>
</template>

<script>
import { formatDistanceToNow } from 'date-fns'
import { ArrowLeftIcon, ArrowRightIcon } from 'vue-feather-icons'
import CheckboxTick from '@/assets/images/certification/checkbox-tick.svg'
import FeedbackIcon from '@/assets/images/certification/message-circle.svg'
import PinIcon from '@/assets/images/certification/pin-icon.svg'
import AnswerTextbox from './AnswerTextbox.vue'

export default {
  name: 'Answers',
  components: {
    ArrowLeftIcon,
    ArrowRightIcon,
    CheckboxTick: { ...CheckboxTick, name: 'CheckboxTick' },
    FeedbackIcon: { ...FeedbackIcon, name: 'FeedbackIcon' },
    PinIcon: { ...PinIcon, name: 'PinIcon' },
    AnswerTextbox,
  },
  props: {
    currentQuestion: {
      type: Object,
      required: true,
    },
    lastQuestion: {
      type: Object,
      required: true,
    },
    firstQuestion: {
      type: Object,
      required: true,
    },
    showActionButtons: {
      type: Boolean,
      required: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
    },
    lastSavedDate: {
      type: Date,
      required: false,
    },
    isBeta: {
      type: Boolean,
      required: false,
    },
    showFinished: {
      type: Boolean,
      required: false,
      default: false,
    },
    examLayoutV2: {
      type: Boolean,
      required: false,
      default: false,
    },
    attempt: {
      type: Object,
      required: false,
    },
    isLabRunning: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      currentSelection: [],
      savingTimeIntervalId: null,
      humanSavedTime: '',
      jumpQuestionEnabled: true,
    }
  },
  computed: {
    answers() {
      const { answers } = this.currentQuestion
      return answers
    },
    currentQuestionId() {
      return this.currentQuestion.question_id || this.currentQuestion.uuid
    },
    lastQuestionId() {
      return this.lastQuestion.question_id || this.lastQuestion.uuid
    },
    isLastQuestion() {
      return this.currentQuestionId === this.lastQuestionId
    },
    firstQuestionId() {
      return this.firstQuestion.question_id || this.firstQuestion.uuid
    },
    hasFeedback() {
      return !!this.currentQuestion.feedback
    },
    hasFlagged() {
      return !!this.currentQuestion.is_flagged
    },
    answerTypeText() {
      return this.currentQuestion.type === 'text'
    },
    tooltipText() {
      return this.hasFlagged
        ? 'Unpin'
        : 'Pin this question as a reminder to come back later'
    },
    nextButtonLabel() {
      if (this.isLastQuestion) return 'Finish'
      return this.examLayoutV2 ? 'Next' : 'Next question'
    },
    previousButtonLabel() {
      return this.examLayoutV2 ? 'Previous' : 'Previous question'
    },
  },
  watch: {
    currentQuestion(question, oldQuestion) {
      if (
        (oldQuestion.uuid || oldQuestion.question_id) !== this.currentQuestionId
      ) {
        this.currentSelection = []
        this.updateSelectedAnswers()
      }
    },
    lastSavedDate(value) {
      this.updateSavedTime()
    },
    currentSelection() {
      if (this.currentQuestion.answers) {
        this.currentQuestion.answers.map(a => {
          a.is_selected = this.currentSelection.includes(this.answerId(a))
        })
      } else {
        return [this.currentQuestion.question_id || this.currentQuestion.uuid]
      }
    },
  },
  beforeDestroy() {
    this.savingTimeIntervalId = clearInterval(this.savingTimeIntervalId)
  },
  mounted() {
    this.updateSelectedAnswers()
    this.savingTimeIntervalId = setInterval(this.updateSavedTime, 60000)
  },
  methods: {
    answerContent(answer) {
      return answer.content || answer.answer_html
    },
    updateSelection(id) {
      if (!this.currentSelection.includes(id)) {
        this.currentSelection = [...this.currentSelection, id]
      } else {
        this.currentSelection = this.currentSelection.filter(
          selectionId => selectionId !== id
        )
      }
    },
    isSelected(answer) {
      return this.currentSelection.includes(answer.answer_id || answer.uuid)
    },
    previousQuestion() {
      this.$scrollTo('.quiz-content__wrapper', 10, {
        container: '.quiz-content__modal',
        offset: 0,
      })
      this.$emit('move-previous-question', this.currentSelection)
    },
    answerQuestion() {
      this.$scrollTo('.quiz-content__wrapper', 10, {
        container: '.quiz-content__modal',
        offset: 0,
      })
      this.$emit('answer-selected', this.currentSelection)
    },
    updateSelectedAnswers() {
      if (!Array.isArray(this.currentSelection)) {
        this.currentSelection = [this.currentSelection]
      }
      let apiSelectedQuestion = []
      if (this.currentQuestion.answers) {
        apiSelectedQuestion = this.currentQuestion.answers.reduce(
          (acc, answer) => {
            if (answer.is_selected_by_user || answer.is_selected)
              acc.push(answer.answer_id || answer.uuid)
            return acc
          },
          []
        )
      } else {
        if (this.currentQuestion.user_answer) {
          apiSelectedQuestion = [this.currentQuestion.uuid]
        }
      }
      this.currentSelection = apiSelectedQuestion
    },
    answerId(answer) {
      return answer.answer_id || answer.uuid
    },
    updateSavedTime() {
      if (this.lastSavedDate) {
        this.humanSavedTime = formatDistanceToNow(this.lastSavedDate, {
          addSuffix: true,
        })
      }
    },
    setDisabled(value) {
      this.jumpQuestionEnabled = value
      this.$emit('set-disabled', value)
    },
  },
}
</script>

<style lang="scss">
.answers {
  margin: 20px 0 0 0;
  color: #cecece;
  @include desktop {
    margin: 0;
    padding: 0 20px 15px 20px;
  }
  &__selection {
    display: flex;
    margin: 0 0 10px 0;
    border: 1px solid var(--tundora-gray);
    border-radius: 10px;
    cursor: pointer;
    &--selected {
      border-color: var(--silver-chalice-2);
      color: var(--white);
    }
  }
  &__select {
    background: none;
    border: none;
    margin: 0;
    position: relative;
    width: 100%;
    &:hover {
      cursor: pointer;
    }
  }
  &__action-buttons {
    display: flex;
    justify-content: space-around;
    min-width: 145px;
    align-items: center;
    &__wrapper {
      background: var(--glass-mid-gray);
      width: 60px;
      border-radius: 30px;
      height: 60px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: 0.2s all ease;
      svg {
        path {
          transition: 0.2s all ease;
        }
      }
      &:hover:not(&-pin) {
        background: var(--menu-subcategory);
        svg {
          path {
            stroke: var(--white);
            fill: none;
          }
        }
      }
      &-pin:not(&--active) {
        &:hover {
          background: var(--white);
        }
        svg {
          rect {
            fill-opacity: 0;
          }
        }
      }
      &--active {
        background: transparent
          linear-gradient(180deg, #ef7d22 0%, #ee3b00 100%) 0% 0% no-repeat
          padding-box;
        svg {
          path {
            stroke: var(--white);
            fill: var(--white);
          }
        }
      }
    }

    &__with-time {
      display: flex;
      flex-direction: column-reverse;
      align-items: flex-end;
      justify-content: flex-end;

      @include tablet {
        flex-direction: column-reverse;
      }
      gap: 26px;
    }
  }
  &__icon {
    position: absolute;
    top: -1px;
    right: -1px;
    &--filled {
      fill: var(--green);
    }
  }
  &__answer {
    font-size: 1rem;
    margin: 0 0 0 20px;
    font-family: var(--font-book);
    font-size: rem(16px);
    font-style: normal;
    font-weight: 400;
    line-height: rem(24px);
    pre {
      margin: 0;
    }
    span pre code {
      white-space: pre-line;
    }
    p {
      margin: 0;
    }
  }
  &__btn {
    cursor: pointer;
    &-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-wrap: wrap;
      position: absolute;
      right: 0;
      left: 0;
      bottom: 0;
      margin-top: 30px;
      padding: 25px 30px 25px 30px;
      min-height: 110px;
      border-top: 1px solid #424242;
      &--layout-v2 {
        border-top: none;
        position: relative;
        padding: unset;
        min-height: auto;
        align-items: flex-start;
        margin-top: 50px;
      }
    }
    &__next {
      order: 1;
    }
  }
  &__saving {
    display: flex;
    align-items: center;
    width: 100px;
    justify-content: space-around;
    margin-right: 10px;
    padding: 19px 0 20px 0;
    &-wrapper {
      justify-content: flex-end;
    }
  }
  &__saved-time {
    width: 100%;
    text-align: right;
    font-size: 0.75rem;
    color: var(--silver-chalice);
    margin-top: 20px;
    padding-right: 10px;
    order: 0;
    position: relative;
    @include tablet {
      margin: 26px 0 30px 0;
      font-size: 0.75rem;
      padding-right: 0;
    }
    @include lg-desktop {
      font-size: 0.85rem;
    }
    &--highlighted {
      margin-right: 5px;
      color: var(--white);
      animation-delay: 2s;
      animation-duration: 2s;
      animation-name: fadeOut;
      animation-fill-mode: forwards;
      svg {
        margin-right: 5px;
        path {
          stroke: var(--green);
        }
      }
      @keyframes fadeOut {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
    }
    &--last-question {
      @include tablet {
        width: 100%;
        .answers__saved-time--highlighted {
          right: 10px;
        }
      }
    }
  }
  img {
    max-width: 450px;
  }
}
</style>
